/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { WizardSummary, useGlobalLang } from '@veeam-vspc/shared/components';

import type { WizardStep, WizardStepData } from '@veeam-vspc/shared/components';
import type { VspcLang } from 'configs/languages';

import { StepLayout } from 'components/layouts/StepLayout';
import { UserRoles } from 'core/enums';
import { ManagedCompaniesMode } from 'views/pages/RolesAndUsersPage/components/AdminUser/enums/managed-companies-mode';

import type { SSORuleModel } from '../../../Base/interfaces/sso-rule-model';

export const Summary = ({ data }: WizardStepData<SSORuleModel>) => {
    const lang = useGlobalLang<VspcLang>();
    const roleMap = {
        [UserRoles.ResellerAdministrator]: lang.SERVICE_PROVIDER_ADMINISTRATOR,
        [UserRoles.ResellerOperator]: lang.SERVICE_PROVIDER_OPERATOR,
        [UserRoles.ResellerUser]: lang.SERVICE_PROVIDER_USER,
        [UserRoles.ResellerInvoiceAuditor]: lang.SERVICE_PROVIDER_INVOICE_AUDITOR,
    };

    return (
        <StepLayout
            title={lang.SUMMARY}
            description={lang.REVIEW_RULE_DETAILS}
        >
            <WizardSummary
                fieldsets={[
                    {
                        title: `${lang.RULE_INFO}:`,
                        fields: [
                            {
                                label: `${lang.NAME}:`,
                                value: data.model.name,
                            },
                        ],
                    },
                    {
                        title: `${lang.ROLE}:`,
                        fields: [
                            {
                                label: `${lang.ROLE}:`,
                                value: roleMap[data.model.role],
                            },
                        ],
                    },
                    data.model.role === UserRoles.ResellerAdministrator && {
                        title: `${lang.COMPANIES}:`,
                        fields: [
                            {
                                label: `${lang.COMPANIES}:`,
                                value: lang.ALL,
                            },
                        ],
                    },
                    (data.model.role === UserRoles.ResellerUser || data.model.role === UserRoles.ResellerOperator) && {
                        title: `${lang.COMPANIES}:`,
                        fields: [
                            {
                                label: `${lang.COMPANIES}:`,
                                value: data.selectAllCompanies === ManagedCompaniesMode.CustomSelection
                                    ? data.model.managedCompaniesUids.length
                                    : lang.ALL,
                            },
                        ],
                    },
                    {
                        title: `${lang.CONDITIONS}:`,
                        fields: [
                            {
                                label: `${lang.NAME}:`,
                                value: data.model.organizationMappingSourceClaimType,
                            },
                            {
                                label: `${lang.PARAMETERS}:`,
                                value: data.model.additionalMappings?.length ?? 0,
                            },
                        ],
                    },
                ]}
            />
        </StepLayout>
    );
};

export const getSummaryStep = (title: string): WizardStep<SSORuleModel> => ({
    title,
    render: data => <Summary {...data} />,
    id: 'summary',
});
